import { useState, useEffect, useCallback, useRef, useLayoutEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet";
import DashboardTopbar from "components/DashboardTopbar";
import DashboardMenuBar from "components/DashboardMenuBar";
import { useAppContext } from "components/Context/AppContext";
import TrackedUploadModal from "./TrackedUploadModal";
import AdhocUploadModal from "./AdhocUploadModal";
import ManageBrokerModal from "./ManageBrokerModal";
import "styles/dashboard.css";
import "./portfolios.css";

const DASHBOARD_API    = process.env.REACT_APP_API_URL || "http://localhost:5050/api";
const POLL_INTERVAL_MS = 5_000;
const POLL_MAX_MS      = 5 * 60_000;
const IN_PROGRESS      = new Set(["Pending", "Processing"]);

function fmtMV(val) {
  if (val == null) return <span style={{ color: "var(--dash-muted)" }}>—</span>;
  return `$${Number(val).toLocaleString("en-US", { maximumFractionDigits: 0 })}`;
}

function StatusBadge({ status }) {
  const s = (status || "").toLowerCase();
  if (s === "success" || s === "processed") return <span className="port-badge port-badge-processed">Processed</span>;
  if (s === "pending")    return <span className="port-badge port-badge-pending">Pending</span>;
  if (s === "processing") return <span className="port-badge port-badge-running">Processing</span>;
  if (s === "error" || s === "failed") return <span className="port-badge port-badge-failed">Failed</span>;
  return <span className="port-badge port-badge-pending">{status || "—"}</span>;
}

function TrashIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
      <polyline points="3 6 5 6 21 6"/>
      <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/>
      <path d="M10 11v6M14 11v6"/>
      <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/>
    </svg>
  );
}

function DeleteCell({ onDelete }) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  useEffect(() => {
    function onDoc(e) {
      if (open && wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  return (
    <div className="port-del-wrap" ref={wrapRef}>
      <button className="port-btn-icon" onClick={() => setOpen((v) => !v)}>
        <TrashIcon />
      </button>
      {open && (
        <div className="port-del-confirm open">
          <div className="port-del-confirm-msg">Delete this portfolio? This cannot be undone.</div>
          <div className="port-del-confirm-btns">
            <button className="port-del-cancel" onClick={() => setOpen(false)}>Cancel</button>
            <button className="port-del-ok" onClick={() => { setOpen(false); onDelete(); }}>Delete</button>
          </div>
        </div>
      )}
    </div>
  );
}

export default function PortfoliosPage() {
  const { selectedAccount } = useAppContext();
  const navigate = useNavigate();

  const [theme, setTheme] = useState(() => localStorage.getItem("trg_theme") || "dark");
  const [brokerSettings,   setBrokerSettings]   = useState([]);
  const [brokerFeeds,      setBrokerFeeds]       = useState([]);
  const [trackedPortfolios, setTrackedPortfolios] = useState([]);
  const [positionHistory,  setPositionHistory]  = useState([]);
  const [adhocPortfolios,  setAdhocPortfolios]  = useState([]);
  const [historyFilter,     setHistoryFilter]     = useState("all");
  const [historyMonth,      setHistoryMonth]      = useState(() => String(new Date().getMonth() + 1).padStart(2, "0"));
  const [historyYear,       setHistoryYear]       = useState("");
  const [historyRefreshing, setHistoryRefreshing] = useState(false);

  const [trackedModalOpen, setTrackedModalOpen] = useState(false);
  const [adhocModalOpen,   setAdhocModalOpen]   = useState(false);
  const [brokerModalOpen,  setBrokerModalOpen]  = useState(false);

  const watchedIdsRef    = useRef(new Set());
  const pollRef          = useRef(null);
  const pollStartRef     = useRef(null);
  const pollFnRef        = useRef(null);

  const token = () => sessionStorage.getItem("token");

  function stopPolling() {
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
  }
  function startPolling() {
    if (pollRef.current) return;
    pollStartRef.current = Date.now();
    pollRef.current = setInterval(() => pollFnRef.current(), POLL_INTERVAL_MS);
  }

  useLayoutEffect(() => {
    pollFnRef.current = () => {
      if (Date.now() - pollStartRef.current >= POLL_MAX_MS) {
        stopPolling();
        setAdhocPortfolios((prev) =>
          prev.map((p) => IN_PROGRESS.has(p.status) ? { ...p, status: "Timeout" } : p)
        );
        return;
      }
      const t = token();
      if (!t) return;
      Promise.all([
        fetch(`${DASHBOARD_API}/portfolios/adhoc?token=${t}`).then((r) => r.json()),
        selectedAccount
          ? fetch(`${DASHBOARD_API}/portfolios/tracked?token=${t}&account_id=${selectedAccount.account_id}`).then((r) => r.json())
          : Promise.resolve([]),
        selectedAccount
          ? fetch(`${DASHBOARD_API}/position-history?token=${t}&account_id=${selectedAccount.account_id}`).then((r) => r.json())
          : Promise.resolve(null),
      ]).then(([adhoc, tracked, hist]) => {
        if (Array.isArray(adhoc))    setAdhocPortfolios(adhoc);
        if (Array.isArray(tracked))  setTrackedPortfolios(tracked);
        if (Array.isArray(hist))     setPositionHistory(hist);
        const stillActive = (adhoc || []).some(
          (p) => watchedIdsRef.current.has(p.port_id) && IN_PROGRESS.has(p.status)
        );
        if (!stillActive) stopPolling();
      }).catch(console.error);
    };
  });

  useEffect(() => () => stopPolling(), []);

  const fetchAll = useCallback(() => {
    stopPolling();
    watchedIdsRef.current.clear();
    const t = token();
    if (!t) return;
    const aid = selectedAccount?.account_id;
    const requests = [
      fetch(`${DASHBOARD_API}/broker/settings?token=${t}&account_id=${aid}`).then((r) => r.json()),
      fetch(`${DASHBOARD_API}/broker/feeds?token=${t}&account_id=${aid}`).then((r) => r.json()),
      aid
        ? fetch(`${DASHBOARD_API}/portfolios/tracked?token=${t}&account_id=${aid}`).then((r) => r.json())
        : Promise.resolve([]),
      fetch(`${DASHBOARD_API}/portfolios/adhoc?token=${t}`).then((r) => r.json()),
      aid
        ? fetch(`${DASHBOARD_API}/position-history?token=${t}&account_id=${aid}`).then((r) => r.json())
        : Promise.resolve([]),
    ];
    Promise.all(requests).then(([settings, feeds, tracked, adhoc, hist]) => {
      if (Array.isArray(settings)) setBrokerSettings(settings);
      if (Array.isArray(feeds))    setBrokerFeeds(feeds);
      if (Array.isArray(tracked))  setTrackedPortfolios(tracked);
      if (Array.isArray(adhoc)) {
        setAdhocPortfolios(adhoc);
        if (adhoc.some((p) => IN_PROGRESS.has(p.status))) startPolling();
      }
      if (Array.isArray(hist)) setPositionHistory(hist);
    }).catch(console.error);
  }, [selectedAccount]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const refreshHistory = useCallback(() => {
    const t   = token();
    const aid = selectedAccount?.account_id;
    if (!t || !aid) return;
    setHistoryRefreshing(true);
    fetch(`${DASHBOARD_API}/position-history?token=${t}&account_id=${aid}`)
      .then((r) => r.json())
      .then((hist) => { if (Array.isArray(hist)) setPositionHistory(hist); })
      .catch(console.error)
      .finally(() => setHistoryRefreshing(false));
  }, [selectedAccount]);

  const handleTrackedSubmit = async ({ file, description }) => {
    const t   = token();
    const aid = selectedAccount?.account_id;
    const fd  = new FormData();
    fd.append("file",        file);
    fd.append("type",        "tracked");
    fd.append("description", description || "");
    const res   = await fetch(`${DASHBOARD_API}/portfolios/upload?token=${t}&account_id=${aid}`, { method: "POST", body: fd });
    const entry = await res.json();
    if (res.ok) {
      setTrackedModalOpen(false);
      fetchAll();
    } else {
      throw new Error(entry.error || "Upload failed.");
    }
  };

  const handleAdhocSubmit = async ({ name, file }) => {
    const t   = token();
    const aid = selectedAccount?.account_id;
    const fd  = new FormData();
    fd.append("file", file);
    fd.append("name", name);
    fd.append("type", "adhoc");
    const res   = await fetch(`${DASHBOARD_API}/portfolios/upload?token=${t}&account_id=${aid}`, { method: "POST", body: fd });
    const entry = await res.json();
    if (res.ok) {
      watchedIdsRef.current.add(String(entry.id || entry.port_id));
      setAdhocPortfolios((prev) => [entry, ...prev]);
      setAdhocModalOpen(false);
      startPolling();
    } else {
      throw new Error(entry.error || "Upload failed.");
    }
  };

  const handleDeleteAdhoc = async (portId) => {
    const t   = token();
    const aid = selectedAccount?.account_id;
    await fetch(`${DASHBOARD_API}/portfolios/${portId}?token=${t}&account_id=${aid}`, { method: "DELETE" });
    setAdhocPortfolios((prev) => prev.filter((p) => p.port_id !== portId));
  };

  const handleRemoveBroker = async (id) => {
    const t   = token();
    const aid = selectedAccount?.account_id;
    await fetch(`${DASHBOARD_API}/broker/settings/${id}?token=${t}&account_id=${aid}`, { method: "DELETE" });
    setBrokerSettings((prev) => prev.filter((s) => s.id !== id));
  };

  const handleBrokerAuthSubmit = async ({ broker, account }) => {
    const t   = token();
    const res = await fetch(`${DASHBOARD_API}/broker/request-auth?token=${t}`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ broker, account, account_id: selectedAccount?.account_id }),
    });
    const d = await res.json();
    if (res.ok) {
      setBrokerSettings((prev) => [d.setting, ...prev]);
    } else {
      alert(d.error || "Request failed.");
    }
  };

  const accountName = selectedAccount?.account_name || "Your Account";

  // ── Manual positions card summary (latest tracked upload)
  const latestTracked = trackedPortfolios[0] || null;

  // ── Broker feed summary
  const activeFeeds  = brokerSettings.filter((s) => (s.status || "").toLowerCase() === "active").length;
  const pendingFeeds = brokerSettings.filter((s) => (s.status || "").toLowerCase() !== "active").length;

  // ── Filtered history rows
  const historyYears = [...new Set(positionHistory.map((r) => r.datetime?.trim().slice(0, 4)).filter(Boolean))].sort().reverse();

  const visibleHistory = positionHistory.filter((r) => {
    if (historyFilter !== "all" && r.source !== historyFilter) return false;
    const dt = r.datetime ? r.datetime.trim() : "";
    if (historyYear  && dt.slice(0, 4) !== historyYear)  return false;
    if (historyMonth && dt.slice(5, 7) !== historyMonth) return false;
    return true;
  });

  return (
    <>
      <Helmet><title>Portfolios — Tail Risk Global</title></Helmet>
      <div className={`trg-shell${theme === "light" ? " theme-light" : ""}`}>
        <DashboardTopbar />
        <DashboardMenuBar onThemeChange={setTheme} />

        <main className="port-body">

          {/* Page header */}
          <div className="port-page-hdr">
            <div>
              <div className="port-kicker">{accountName} · Portfolio Manager</div>
              <div className="port-title">Portfolios</div>
            </div>
          </div>

          {/* ═══ ZONE 1: TRACKED POSITIONS ═══ */}
          <div className="port-zone">
            <div className="port-zone-hdr">
              <div className="port-zone-title-group">
                <div className="port-zone-title">Tracked Positions</div>
                <span className="port-zone-badge port-zone-badge-tracked">Daily risk calculation</span>
              </div>
            </div>

            {/* Source cards */}
            <div className="port-source-cards">

              {/* Broker Feed card */}
              <div className="port-source-card">
                <div className="port-source-card-top">
                  <div className="port-source-card-icon-title">
                    <div className="port-source-card-icon port-source-card-icon-broker">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/>
                        <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/>
                      </svg>
                    </div>
                    <div>
                      <div className="port-source-card-title">Broker Feed</div>
                      <div className="port-source-card-sub">Positions arrive automatically each trading day</div>
                    </div>
                  </div>

                  {brokerSettings.length === 0 ? (
                    <div style={{ fontFamily: "var(--dash-font-b)", fontSize: "0.57rem", color: "var(--dash-muted)" }}>
                      No broker connections configured.
                    </div>
                  ) : (
                    <div className="port-broker-list">
                      {brokerSettings.map((s) => {
                        const feed = brokerFeeds.find((f) => f.account === s.account);
                        return (
                          <div key={s.id} className="port-broker-item">
                            <div>
                              <div className="port-broker-name">{s.broker}</div>
                              <div className="port-broker-acct">{s.account}{s.name ? ` · ${s.name}` : ""}</div>
                            </div>
                            <div className="port-broker-item-right">
                              {(s.status || "").toLowerCase() === "active"
                                ? <span className="port-badge port-badge-active">Active</span>
                                : <span className="port-badge port-badge-setup">Pending Setup</span>
                              }
                              <div className="port-broker-lastsync">
                                {feed ? `Last: ${feed.last_feed}` : "—"}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
                <div className="port-source-card-footer">
                  <div className="port-source-card-footer-note">
                    <strong>{activeFeeds} active</strong>
                    {pendingFeeds > 0 && ` · ${pendingFeeds} pending setup`}
                  </div>
                  <button className="port-btn port-btn-primary" style={{ fontSize: "0.5rem", padding: "0.3rem 0.7rem" }} onClick={() => setBrokerModalOpen(true)}>
                    Manage Connections
                  </button>
                </div>
              </div>

              {/* Manual Positions card */}
              <div className="port-source-card">
                <div className="port-source-card-top">
                  <div className="port-source-card-icon-title">
                    <div className="port-source-card-icon port-source-card-icon-manual">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                        <polyline points="17 8 12 3 7 8"/>
                        <line x1="12" y1="3" x2="12" y2="15"/>
                      </svg>
                    </div>
                    <div>
                      <div className="port-source-card-title">Manual Positions</div>
                      <div className="port-source-card-sub">Uploaded when positions change outside the broker feed</div>
                    </div>
                  </div>

                  {latestTracked ? (
                    <>
                      <div className="port-manual-stats">
                        <div className="port-manual-stat">
                          <div className="port-manual-stat-label">Last Upload</div>
                          <div className="port-manual-stat-value">{latestTracked.upload_dt}</div>
                        </div>
                        <div className="port-manual-stat">
                          <div className="port-manual-stat-label">Uploaded By</div>
                          <div className="port-manual-stat-value">{latestTracked.uploaded_by}</div>
                        </div>
                        <div className="port-manual-stat">
                          <div className="port-manual-stat-label">Positions</div>
                          <div className="port-manual-stat-value">{latestTracked.positions ?? "—"}</div>
                        </div>
                        <div className="port-manual-stat">
                          <div className="port-manual-stat-label">Market Value</div>
                          <div className="port-manual-stat-value">{fmtMV(latestTracked.mv)}</div>
                        </div>
                      </div>
                      {latestTracked.description && (
                        <div className="port-manual-desc">
                          <div className="port-manual-stat-label">Description</div>
                          <div className="port-manual-desc-text">{latestTracked.description}</div>
                        </div>
                      )}
                    </>
                  ) : (
                    <div style={{ fontFamily: "var(--dash-font-b)", fontSize: "0.57rem", color: "var(--dash-muted)" }}>
                      No manual upload yet.
                    </div>
                  )}
                </div>
                <div className="port-source-card-footer">
                  <div className="port-source-card-footer-note">
                    Replaces current snapshot · used from <strong>next daily run</strong>
                  </div>
                  <button className="port-btn port-btn-primary" style={{ fontSize: "0.5rem", padding: "0.3rem 0.7rem" }} onClick={() => setTrackedModalOpen(true)}>
                    Upload Positions
                  </button>
                </div>
              </div>

            </div>{/* /source-cards */}

          </div>{/* /zone-tracked */}


          {/* ═══ ZONE DIVIDER ═══ */}
          <div className="port-zone-divider">
            <div className="port-zone-divider-line"></div>
            <div className="port-zone-divider-center">
              <div className="port-zone-divider-title">Ad-hoc Analysis</div>
              <div className="port-zone-divider-sub">One-time uploads — not tracked, not included in the daily risk run</div>
            </div>
            <div className="port-zone-divider-line"></div>
          </div>


          {/* ═══ ZONE 2: AD-HOC ANALYSIS ═══ */}
          <div className="port-zone">
            <div className="port-zone-hdr">
              <div className="port-zone-title-group">
                <div className="port-zone-title">Ad-hoc Analysis</div>
                <span className="port-zone-badge port-zone-badge-adhoc">One-time · Not tracked</span>
              </div>
              <button className="port-btn port-btn-blue" onClick={() => setAdhocModalOpen(true)}>
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                  <polyline points="16 16 12 12 8 16"/><line x1="12" y1="12" x2="12" y2="21"/>
                  <path d="M20.39 18.39A5 5 0 0 0 18 9h-1.26A8 8 0 1 0 3 16.3"/>
                </svg>
                Upload for Analysis
              </button>
            </div>

            <div className="port-adhoc-callout">
              <div className="port-adhoc-callout-icon">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
                </svg>
              </div>
              <div>
                <div className="port-adhoc-callout-title">One-time analysis only</div>
                <div className="port-adhoc-callout-text">
                  Portfolios uploaded here are <strong>not tracked</strong> and will <strong>not affect {accountName}'s daily risk report</strong>.
                  Use this for what-if scenarios, stress tests, or any portfolio you want to analyse without committing it as the fund's live positions.
                  Results are available in the <strong>What-If</strong> page.
                </div>
              </div>
            </div>

            <div className="port-table-card">
              <div className="port-table-wrap">
                <table className="port-table">
                  <thead>
                    <tr>
                      <th>ID</th>
                      <th>Portfolio Name</th>
                      <th>File</th>
                      <th>Uploaded</th>
                      <th>As of Date</th>
                      <th className="right">Market Value</th>
                      <th className="right"># Positions</th>
                      <th className="center">Status</th>
                      <th className="center">What-If</th>
                      <th className="center">Download</th>
                      <th className="center">Delete</th>
                    </tr>
                  </thead>
                  <tbody>
                    {adhocPortfolios.length === 0 ? (
                      <tr>
                        <td colSpan={11} style={{ textAlign: "center", color: "var(--dash-muted)", padding: "1.5rem" }}>
                          No ad-hoc portfolios yet.
                        </td>
                      </tr>
                    ) : adhocPortfolios.map((p) => {
                      const processed = (p.status || "").toLowerCase() === "success" || (p.status || "").toLowerCase() === "processed";
                      return (
                        <tr key={p.port_id}>
                          <td className="mono">{p.id}</td>
                          <td className="pname">{p.name}</td>
                          <td><span className="fname">{p.file}</span></td>
                          <td className="muted">{p.upload_dt}</td>
                          <td className="muted">{p.as_of_date}</td>
                          <td className="right">{fmtMV(p.mv)}</td>
                          <td className="right mono">{p.positions ?? <span style={{ color: "var(--dash-muted)" }}>—</span>}</td>
                          <td className="center"><StatusBadge status={p.status} /></td>
                          <td className="center">
                            <a
                              href="#"
                              className={`whif-link${!processed ? " disabled" : ""}`}
                              onClick={(e) => {
                                e.preventDefault();
                                if (processed) navigate(`/whatif?add_port_id=${p.port_id}`);
                              }}
                            >
                              View in What-If →
                            </a>
                          </td>
                          <td className="center">
                            {p.file ? (
                              <a
                                href={`${DASHBOARD_API}/portfolios/${p.port_id}/download?token=${token()}`}
                                className="dl-link"
                              >
                                ↓ Download
                              </a>
                            ) : (
                              <span style={{ color: "var(--dash-muted)", fontSize: "0.55rem" }}>—</span>
                            )}
                          </td>
                          <td className="center">
                            <DeleteCell onDelete={() => handleDeleteAdhoc(p.port_id)} />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>

          </div>{/* /zone-adhoc */}


          {/* ═══ ZONE DIVIDER ═══ */}
          <div className="port-zone-divider">
            <div className="port-zone-divider-line"></div>
            <div className="port-zone-divider-center">
              <div className="port-zone-divider-title">Position History</div>
              <div className="port-zone-divider-sub">All position snapshots received — broker feeds and manual uploads</div>
            </div>
            <div className="port-zone-divider-line"></div>
          </div>


          {/* ═══ POSITION HISTORY ═══ */}
          <div className="port-zone">
            <div>
              <div className="port-section-hdr" style={{ marginBottom: "0.6rem" }}>
                <div>
                  <div className="port-section-title">Position History</div>
                  <div className="port-section-sub">All position snapshots received — broker feeds and manual uploads</div>
                </div>
              </div>
              <div className="port-filter-bar">
                <span className="port-filter-label">Source</span>
                <button className={`port-filter-btn${historyFilter === "all"    ? " f-all"    : ""}`} onClick={() => setHistoryFilter("all")}>All</button>
                <button className={`port-filter-btn${historyFilter === "broker" ? " f-broker" : ""}`} onClick={() => setHistoryFilter("broker")}>Broker only</button>
                <button className={`port-filter-btn${historyFilter === "manual" ? " f-manual" : ""}`} onClick={() => setHistoryFilter("manual")}>Manual only</button>

                <span className="port-filter-divider" />

                <span className="port-filter-label">Period</span>
                <select
                  className="port-filter-select"
                  value={historyMonth}
                  onChange={(e) => setHistoryMonth(e.target.value)}
                >
                  <option value="">All months</option>
                  {["January","February","March","April","May","June","July","August","September","October","November","December"].map((m, i) => (
                    <option key={m} value={String(i + 1).padStart(2, "0")}>{m}</option>
                  ))}
                </select>
                <select
                  className="port-filter-select"
                  value={historyYear}
                  onChange={(e) => setHistoryYear(e.target.value)}
                >
                  <option value="">All years</option>
                  {historyYears.map((y) => <option key={y}>{y}</option>)}
                </select>
                <button
                  className="port-refresh-btn"
                  style={{ marginLeft: "auto" }}
                  onClick={refreshHistory}
                  disabled={historyRefreshing}
                  title="Refresh position history"
                >
                  <span className={historyRefreshing ? "port-refresh-spin" : ""}>↻</span>
                  {historyRefreshing ? "Refreshing…" : "Refresh"}
                </button>
              </div>
              <div className="port-table-card">
                <div className="port-table-wrap">
                  <table className="port-table">
                    <thead>
                      <tr>
                        <th>Date / Time</th>
                        <th>Source</th>
                        <th>As of Date</th>
                        <th className="right">Market Value</th>
                        <th className="right"># Positions</th>
                        <th className="right"># Securities</th>
                        <th className="center">Status</th>
                        <th className="center">Download</th>
                      </tr>
                    </thead>
                    <tbody>
                      {visibleHistory.length === 0 ? (
                        <tr>
                          <td colSpan={8} style={{ textAlign: "center", color: "var(--dash-muted)", padding: "1.5rem" }}>
                            No position history available.
                          </td>
                        </tr>
                      ) : visibleHistory.map((row, i) => (
                        <tr key={i}>
                          <td className="muted">{row.datetime}</td>
                          <td>
                            <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
                              {row.source === "broker"
                                ? <span className="port-source-tag port-source-tag-broker">Broker</span>
                                : <span className="port-source-tag port-source-tag-manual">Manual</span>
                              }
                              <span className="fname">{row.source_detail}</span>
                            </div>
                          </td>
                          <td className="muted">{row.as_of_date}</td>
                          <td className="right">{fmtMV(row.mv)}</td>
                          <td className="right mono">{row.positions ?? <span style={{ color: "var(--dash-muted)" }}>—</span>}</td>
                          <td className="right mono">{row.securities ?? <span style={{ color: "var(--dash-muted)" }}>—</span>}</td>
                          <td className="center"><StatusBadge status={row.status} /></td>
                          <td className="center">
                            {row.port_id ? (
                              <a
                                href={`${DASHBOARD_API}/portfolios/${row.port_id}/download?token=${token()}`}
                                className="dl-link"
                              >
                                ↓ Download
                              </a>
                            ) : (
                              <span style={{ color: "var(--dash-muted)", fontSize: "0.55rem" }}>—</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>{/* /zone-history */}

        </main>

        <TrackedUploadModal
          isOpen={trackedModalOpen}
          onClose={() => setTrackedModalOpen(false)}
          onSubmit={handleTrackedSubmit}
          accountName={accountName}
        />
        <AdhocUploadModal
          isOpen={adhocModalOpen}
          onClose={() => setAdhocModalOpen(false)}
          onSubmit={handleAdhocSubmit}
          accountName={accountName}
        />
        <ManageBrokerModal
          isOpen={brokerModalOpen}
          onClose={() => setBrokerModalOpen(false)}
          settings={brokerSettings}
          onRemove={handleRemoveBroker}
          onAdd={handleBrokerAuthSubmit}
          accountName={accountName}
        />
      </div>
    </>
  );
}
